/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { getExtensionPropertyValue } from '../../../../modules/utilities/Helper';
import {
    IPriceComponentResources,
    //ISwatchItem,
    //ProductComponentSwatchComponent,
    //RatingComponent,
    AddToCartComponent
} from '@msdyn365-commerce/components';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    //AttributeValue,
    CommerceProperty,
    ProductDimension,
    ProductSearchResult,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    //convertDimensionTypeToProductDimensionType,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    IProductInventoryInformation,
    getSelectedVariant,
    SelectedVariantInput,
    IDimensionForSelectedVariant,
    // Dictionary,
    // DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp
    //StringExtensions
    //validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Node
    //onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import { customcutInterface } from '../../../../modules/common/customcutInterface';
import getProductAvailabilitiesForSelectedVariant, {
    ProductAvailabilitiesForSelectedVariantInput
} from '../../../../actions/get-product-availabilities-for-selected-variant';
import { getInventoryAsync, getLeadTimeInformationAsync } from '../../../../actions/DataActionExtension.g';
import { ILeadTimeDataEntity } from '../../../../actions/DataServiceEntities.g';
import SlitCoilPopup from '../../../../modules/utilities/CustomSlitPopup';
import ReactDOM from 'react-dom';
import { getUnitsOfMeasureAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    isLeadTimeEnabled?: boolean;
    showCustomerPartNumber?: boolean;
    defaultLeadTime?: number;
    isCustomerPickupEnabled?: boolean;
    customerDeliveryMode?: string;
    showUnitOfMeasure?: boolean;
}

export interface IListAddToCartViewProps {
    ContainerProps: INodeProps;
    button?: React.ReactNode;
}

export interface ProductConversionMatrix {
    PRODUCT: number;
    FACTOR: number;
    FromUnit: string;
    ToUnit: string;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    isLeadTimeEnabled,
    defaultLeadTime,
    showCustomerPartNumber,
    isCustomerPickupEnabled,
    customerDeliveryMode,
    showUnitOfMeasure
}) => {
    const product = data.product;
    const [quantity, setQuantity] = useState(0);
    const [maxQuantity, setMaxQuantity] = useState(0);
    const [maxAvailable, setMaxAvailable] = useState(0);
    const [qtyReadonly, setQtyReadOnly] = useState<boolean>(false);
    const [qtyStep, setQtyStep] = useState<number>(1);
    const [selectedUnit, setSelectedUnit] = useState<string>(product?.DefaultUnitOfMeasure || '');
    const [unitsOfMeasure, setUnitsOfMeasure] = useState<string[]>([]);
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    //const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        //setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    //const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    //const enableStockCheck = context.app.config.enableStockCheck;
    //const siteContext = context as ICoreContext<IDimensionsApp>;
    //const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
    const [dimensionValues, setDimensionValues] = useState<ProductDimension[]>([]);
    const [prodDimensions, setProdDimensions] = useState<IDimensionForSelectedVariant[]>([]);
    const [defaultDimensions, setDefaultDimensions] = useState<IDimensionForSelectedVariant[]>([]);
    const [customerPartNumber, setCustomerPartNumber] = useState<string | undefined>(undefined);
    const [cartProduct, setCartProduct] = useState<SimpleProduct>();
    const [productAvailableQuantity, setProductAvailableQuantity] = useState<IProductInventoryInformation>();
    const customEnabled = getExtensionPropertyValue(product, 'CUSTOMCUTENABLED');
    //const maxLength = parseFloat(getExtensionPropertyValue(product, 'MAXLENGTH')!.toString());
    //const maxWidth = parseFloat(getExtensionPropertyValue(product, 'MAXWIDTH')!.toString());
    const [maxLength, setMaxLength] = useState(
        parseFloat(
            getExtensionPropertyValue(product, 'MAXLENGTH') === undefined
                ? '0'
                : getExtensionPropertyValue(product, 'MAXLENGTH')!.toString()
        )
    );
    const [maxWidth, setMaxWidth] = useState(
        parseFloat(
            getExtensionPropertyValue(product, 'MAXWIDTH') === undefined ? '0' : getExtensionPropertyValue(product, 'MAXWIDTH')!.toString()
        )
    );
    // const productGrade = getExtensionPropertyValue(product, 'PRODUCTGRADE')?.toString() ?? '';
    // const productCondition = getExtensionPropertyValue(product, 'PRODUCTCONDITION')?.toString() ?? '';

    const [customLengthEnabled, setCustomLengthEnabled] = useState<boolean>(false);
    const [customWidthEnabled, setCustomWidthEnabled] = useState<boolean>(false);
    const [customLength, setCustomLength] = useState<number>(0);
    const [customWidth, setCustomWidth] = useState<number>(0);
    const [productLength, setProductLength] = useState<number>(0);
    const [productWidth, setProductWidth] = useState<number>(0);
    const [leadTimeData, setLeadTimeData] = useState<ILeadTimeDataEntity[]>([]);
    const [leadTime, setLeadTime] = useState<string>('');
    const isMobile = context.request.device.Type === 'Mobile';
    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
    React.useEffect(() => {
        if (isPopupOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isPopupOpen]);
    function formatDateMMDDYY(date: Date): string {
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yy = String(date.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
    }

    function getCumulativeLeadTimeByHighLead(
        quantity: number,
        data: ILeadTimeDataEntity[]
    ): {
        UsedWarehouses: ILeadTimeDataEntity[];
        FinalLowLeadDateTime: string;
        FinalHighLeadDateTime: string;
    } | null {
        // Sort by shortest HighLeadDateTime
        const sortedData = [...data].sort((a, b) => a.Priority - b.Priority);

        let totalAccumulated = 0;
        const usedWarehouses: ILeadTimeDataEntity[] = [];

        for (const warehouse of sortedData) {
            if (totalAccumulated >= quantity) break;

            usedWarehouses.push(warehouse);
            totalAccumulated += warehouse.PhysicalInventory;
        }

        if (totalAccumulated < quantity) {
            return null; // Not enough stock
        }

        // Compute final lead times (latest among used)
        const finalLow = usedWarehouses.map(w => new Date(w.LowLeadDateTime!)).reduce((max, curr) => (curr > max ? curr : max));

        const finalHigh = usedWarehouses.map(w => new Date(w.HighLeadDateTime!)).reduce((max, curr) => (curr > max ? curr : max));

        return {
            UsedWarehouses: usedWarehouses,
            FinalLowLeadDateTime: formatDateMMDDYY(finalLow),
            FinalHighLeadDateTime: formatDateMMDDYY(finalHigh)
        };
    }

    React.useEffect(() => {
        if (leadTimeData && leadTimeData.length > 0 && quantity > 0 && quantity <= maxQuantity) {
            const result = getCumulativeLeadTimeByHighLead(quantity, leadTimeData);
            if (result?.FinalLowLeadDateTime === result?.FinalHighLeadDateTime) {
                setLeadTime(`${result?.FinalLowLeadDateTime}`);
            } else {
                setLeadTime(`${result?.FinalLowLeadDateTime}-${result?.FinalHighLeadDateTime}`);
            }
        } else {
            setLeadTime('');
        }
    }, [quantity]);

    React.useEffect(() => {
        async function getSelectedVariantbyDimensions() {
            let variant = await getSelectedVariant(
                new SelectedVariantInput(
                    data.product!.MasterProductId ? data.product?.MasterProductId! : data.product!.RecordId!,
                    context.request.apiSettings.channelId,
                    dimensionValues,
                    undefined,
                    context.request
                ),
                context.actionContext
            );

            if (variant?.RecordId != data.product?.MasterProductId) {
                const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                    new ProductAvailabilitiesForSelectedVariantInput(variant?.RecordId!, context.request.apiSettings.channelId),
                    context.actionContext
                );

                if (newAvailableQuantity && newAvailableQuantity.length > 0) {
                    setProductAvailableQuantity(newAvailableQuantity[0]);
                    if (dimensionValues.some(d => d.DimensionValue!.RecordId === -1)) {
                        var stockEntity = await getInventoryAsync({ callerContext: context.actionContext }, data.product!.ItemId!);
                        if (stockEntity) {
                            setMaxAvailable(stockEntity.AvailableQuantity || 0);
                            setMaxQuantity(stockEntity.AvailableQuantity || 0);
                        }
                    } else {
                        setMaxAvailable(newAvailableQuantity[0].ProductAvailableQuantity.AvailableQuantity || 0);
                        setMaxQuantity(newAvailableQuantity[0].ProductAvailableQuantity.AvailableQuantity || 0);
                    }
                } else {
                    setProductAvailableQuantity(undefined);
                }
                let cartProductVariant: SimpleProduct = {
                    RecordId: variant?.RecordId!,
                    ItemId: data.product?.ItemId,
                    Name: data.product?.Name,
                    Description: data.product?.Description,
                    ProductTypeValue: data.product?.RecordId!,
                    DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
                    BasePrice: data.product?.BasePrice!,
                    Price: data.product?.Price!,
                    AdjustedPrice: data.product?.Price!,
                    MasterProductId: data.product?.MasterProductId,
                    PrimaryImageUrl: data.product?.PrimaryImageUrl,
                    ExtensionProperties: variant?.ExtensionProperties
                };
                setCartProduct(cartProductVariant);
                setCustomerPartNumber(getExtensionPropertyValue(cartProductVariant, 'CustomerPartNumber')?.toString());
                if (context.request.user && context.request.user.isAuthenticated) {
                    getLeadTimeDetails(variant?.RecordId!);
                }
            } else {
                setCartProduct(undefined);
            }
        }

        async function getLeadTimeDetails(productId: number) {
            const inventoryLevel = context.app.config.inventoryLevel;
            var leadTimeInfo = await getLeadTimeInformationAsync(
                { callerContext: context.actionContext },
                context.request.user.customerAccountNumber!,
                customerDeliveryMode || '',
                productId,
                isCustomerPickupEnabled || false,
                inventoryLevel
            );
            if (leadTimeInfo) {
                setLeadTimeData(leadTimeInfo);
                let prodMaxQty = leadTimeInfo.reduce((sum, item) => {
                    return sum + item.PhysicalInventory;
                }, 0);
                setMaxQuantity(prodMaxQty);
            }
        }
        setQuantity(0);
        if (defaultDimensions.length > 0 && defaultDimensions.length == dimensionValues.length) {
            getSelectedVariantbyDimensions();
        } else {
            setCartProduct(undefined);
        }
    }, [dimensionValues, isCustomerPickupEnabled, customerDeliveryMode]);
    React.useEffect(() => {
        if (cartProduct && cartProduct.DefaultUnitOfMeasure !== selectedUnit) {
            const updatedCartProduct = {
                ...cartProduct,
                DefaultUnitOfMeasure: selectedUnit
            };
            setCartProduct(updatedCartProduct);
            updateCustomCartDescription(customLength, customWidth, 0);
            setQuantity(0);
        }
    }, [selectedUnit]);
    React.useEffect(() => {
        async function fetchUnitsOfMeasure() {
            if (cartProduct) {
                const result = await getUnitsOfMeasureAsync({ callerContext: context.actionContext }, cartProduct.RecordId);
                if (result) {
                    const units = result.map(u => u.Symbol!);
                    setUnitsOfMeasure(units);
                    // Highlight default unit initially
                    setSelectedUnit(cartProduct.DefaultUnitOfMeasure || '');
                    //console.log('Units of Measure fetched:', units);
                }
            }
        }
        fetchUnitsOfMeasure();
    }, [cartProduct]);

    function renderUnitOfMeasure() {
        return (
            <>
                <select
                    value={selectedUnit || product?.DefaultUnitOfMeasure || ''}
                    onChange={e => {
                        setSelectedUnit(e.target.value);
                        setQtyReadOnly(e.target.value !== 'pc');
                        setUnitStep(e.target.value);
                        //setQtyStep(1);
                    }}
                >
                    {unitsOfMeasure.map(unit => (
                        <option key={unit} value={unit}>
                            {unit}
                        </option>
                    ))}
                </select>
            </>
        );
    }

    const setUnitStep = (selectedUnit: string) => {
        if (selectedUnit === 'pc') {
            setQtyStep(1);
            setMaxQuantity(maxAvailable);
        } else {
            if (cartProduct && cartProduct.ExtensionProperties?.find(ext => ext.Key === 'CONVERSIONMATRIX')) {
                let conversionValues: ProductConversionMatrix[] = [];
                var extension = cartProduct.ExtensionProperties.find(ext => ext.Key === 'CONVERSIONMATRIX');
                if (extension?.Value?.StringValue) {
                    conversionValues = JSON.parse(extension.Value.StringValue);
                }
                if (conversionValues.length > 0) {
                    var conversionValue = conversionValues.find(cv => {
                        return cv.FromUnit === 'pc' && cv.ToUnit === selectedUnit;
                    });
                    if (conversionValue) {
                        setQuantity(quantity * conversionValue.FACTOR);
                        setQtyStep(conversionValue.FACTOR);
                        setMaxQuantity(maxAvailable * conversionValue.FACTOR);
                    }
                }
            }
        }
    };
    React.useEffect(() => {
        async function getDefaultDimensionsForVariant() {
            if (data.product?.MasterProductId) {
                let prodDimensions = await getDimensionsForSelectedVariant(
                    new GetDimensionsForSelectedVariantInput(
                        data.product?.MasterProductId ?? 0,
                        context.request.apiSettings.channelId,
                        [],
                        context.request
                    ),
                    context.actionContext
                );

                //prodDimensions = prodDimensions.filter(dimension => dimension.DimensionTypeValue !== 2); // Remove Stock Dimension

                prodDimensions = prodDimensions.sort((a, b) => {
                    return b.DimensionTypeValue - a.DimensionTypeValue;
                });

                setDimensionValues(
                    prodDimensions
                        .filter(dimension => dimension.DimensionTypeValue == 2)
                        .map(dimension => {
                            const selectedValue = dimension.DimensionValues?.[0]?.Value || '';
                            const recordId = dimension.DimensionValues?.[0]?.RecordId || 0;
                            return {
                                DimensionTypeValue: dimension.DimensionTypeValue,
                                DimensionValue: {
                                    Value: selectedValue,
                                    RecordId: recordId
                                },
                                ExtensionProperties: dimension.ExtensionProperties
                            };
                        })
                );

                setDefaultDimensions(prodDimensions);
                if (maxLength === 0) {
                    const maxLengthValue = prodDimensions
                        .filter(d => d.DimensionTypeValue === 1)
                        .reduce((max, curr) => {
                            const value = parseFloat(curr.DimensionValues?.[0]?.Value ?? '0');
                            return value > max ? value : max;
                        }, 0);

                    if (maxLengthValue) {
                        setMaxLength(maxLengthValue);
                    }
                }
                if (maxWidth === 0) {
                    const maxWidthValue = prodDimensions
                        .filter(d => d.DimensionTypeValue === 3)
                        .reduce((max, curr) => {
                            const value = parseFloat(curr.DimensionValues?.[0]?.Value ?? '0');
                            return value > max ? value : max;
                        }, 0);

                    if (maxWidthValue) {
                        setMaxWidth(maxWidthValue);
                    }
                }
            }
        }

        getDefaultDimensionsForVariant();
    }, []);

    React.useEffect(() => {
        async function getProductDimensionsForVariant() {
            const mappedDimensions: ProductDimension[] = [];
            defaultDimensions.forEach(dimension => {
                var selectedDimension = dimensionValues.find(dv => dv.DimensionTypeValue == dimension.DimensionTypeValue);
                if (selectedDimension) {
                    dimension.DimensionValues?.forEach(ddv => {
                        if (ddv.RecordId === selectedDimension?.DimensionValue?.RecordId) {
                            mappedDimensions.push({
                                DimensionTypeValue: dimension.DimensionTypeValue,
                                DimensionValue: ddv,
                                ExtensionProperties: dimension.ExtensionProperties
                            });
                        }
                    });
                }
            });

            const prodDimensions = await getDimensionsForSelectedVariant(
                new GetDimensionsForSelectedVariantInput(
                    data.product?.MasterProductId ?? 0,
                    context.request.apiSettings.channelId,
                    mappedDimensions,
                    context.request
                ),
                context.actionContext
            );
            setProdDimensions(prodDimensions);
        }

        // You need to restrict it at some point
        // This is just dummy code and should be replaced by actual
        if (dimensionValues.length > 0) {
            getProductDimensionsForVariant();
        }
    }, [dimensionValues]);

    if (!product) {
        return null;
    }

    const handleDimensionChange = (
        dataTypeValue: number | undefined,
        selectedValue: string,
        recordId: number,
        ExtensionProperties: CommerceProperty[] | undefined
    ) => {
        const selectedDimensionValues = defaultDimensions.find(x => {
            return x.DimensionTypeValue === dataTypeValue;
        });

        if (selectedValue === 'Custom') {
            dataTypeValue === 1 ? setCustomLengthEnabled(true) : setCustomWidthEnabled(true);
        } else {
            setCustomLengthEnabled(false);
            setCustomWidthEnabled(false);
            setCustomLength(0);
            setCustomWidth(0);
        }

        selectedValue = selectedDimensionValues?.DimensionValues!.find(x => {
            return x.RecordId === recordId;
        })?.Value!;
        if (selectedValue) {
            if (dataTypeValue === 1) {
                setProductLength(parseFloat(selectedValue));
            } else if (dataTypeValue === 3) {
                setProductWidth(parseFloat(selectedValue));
            }
        }
        setDimensionValues(previousValue => {
            if (selectedValue == '0' && previousValue.some(obj => obj.DimensionTypeValue === dataTypeValue)) {
                var recordIndex = previousValue.findIndex(item => item.DimensionTypeValue == dataTypeValue);
                previousValue.splice(recordIndex, 1);
                return previousValue;
            }
            const updatedValue = previousValue.map(obj => {
                if (obj.DimensionTypeValue == dataTypeValue) {
                    return {
                        ...obj,
                        DimensionValue: {
                            Value: selectedValue,
                            RecordId: recordId
                        }
                    };
                }
                return obj;
            });

            if (!previousValue.some(obj => obj.DimensionTypeValue === dataTypeValue)) {
                updatedValue.push({
                    DimensionTypeValue: dataTypeValue ?? 0,
                    DimensionValue: {
                        Value: selectedValue,
                        RecordId: recordId
                    },
                    ExtensionProperties: ExtensionProperties
                });
            }

            return updatedValue;
        });
    };

    function renderDimensions() {
        const enforceMaxConstraint = (customValue: number, maxValue: number, dimension: string) => {
            if (customValue > maxValue) {
                customValue = maxValue;
            }
            if (dimension === 'length') {
                setCustomLength(customValue);
                updateCustomCartDescription(customValue, customWidth, quantity);
            } else {
                setCustomWidth(customValue);
                updateCustomCartDescription(customLength, customValue, quantity);
            }
        };
        return (
            <div className='msc-product__dimensions'>
                {defaultDimensions.map(dimension => {
                    if (prodDimensions) {
                        prodDimensions.forEach(pd => {
                            if (pd.DimensionTypeValue == dimension.DimensionTypeValue) {
                                dimension = pd;
                            }
                        });
                    }
                    const dimensionTypeValue =
                        dimension.DimensionTypeValue == 1 ? 'length' : dimension.DimensionTypeValue == 3 ? 'width' : 'configuration';
                    let sortedList = dimension.dimensionValuesWithInventory
                        ? dimension.dimensionValuesWithInventory.sort((a, b) => {
                              return parseFloat(a.DimensionValue?.Value!) - parseFloat(b.DimensionValue?.Value!);
                          })
                        : [];
                    return (
                        dimensionTypeValue !== 'configuration' && (
                            <div>
                                <select
                                    onChange={e =>
                                        handleDimensionChange(
                                            dimension.DimensionTypeValue,
                                            e.target.selectedOptions[0].text,
                                            Number(e.target.value),
                                            dimension.ExtensionProperties
                                        )
                                    }
                                >
                                    <option value={'0'}>{dimensionTypeValue}</option>
                                    {sortedList.map(dv => {
                                        if (dimensionValues) {
                                            let selectedDimension = dimensionValues.find(
                                                d => d.DimensionTypeValue == dimension.DimensionTypeValue
                                            );
                                            if (selectedDimension?.DimensionValue?.RecordId == dv.DimensionValue!.RecordId) {
                                                return (
                                                    <option selected value={dv.DimensionValue!.RecordId!}>
                                                        {selectedDimension.DimensionTypeValue == 1 ||
                                                        selectedDimension.DimensionTypeValue == 3
                                                            ? parseFloat(dv.DimensionValue!.Value!).toString()
                                                            : dv.DimensionValue?.Value!}
                                                    </option>
                                                );
                                            }
                                        }

                                        return dv.PhysicalAvailableInventoryLevelLabel == 'Available' ? (
                                            <option value={dv.DimensionValue!.RecordId!}>
                                                {isNaN(parseFloat(dv.DimensionValue?.Value!))
                                                    ? dv.DimensionValue?.Value!
                                                    : parseFloat(dv.DimensionValue!.Value!).toString()}
                                                {' - In Stock'}
                                            </option>
                                        ) : (
                                            <option disabled value={dv.DimensionValue!.RecordId!}>
                                                {parseFloat(dv.DimensionValue!.Value!).toString()}
                                                {' - Call to Order'}
                                            </option>
                                        );
                                    })}
                                    {customEnabled === 1 && <option value={'-1'}>{'Custom'}</option>}
                                </select>
                                {dimensionTypeValue == 'width'
                                    ? customWidthEnabled && (
                                          <input
                                              className='custom-dimension'
                                              placeholder='Width'
                                              type={'number'}
                                              max={maxWidth}
                                              value={customWidth}
                                              onChange={e => {
                                                  enforceMaxConstraint(parseFloat(e.target.value), maxWidth, 'width');
                                              }}
                                              title={'Enter dimension in inches'}
                                          />
                                      )
                                    : customLengthEnabled && (
                                          <input
                                              className='custom-dimension'
                                              placeholder='Length'
                                              type={'number'}
                                              max={maxLength}
                                              value={customLength}
                                              onChange={e => {
                                                  enforceMaxConstraint(parseFloat(e.target.value), maxLength, 'length');
                                              }}
                                              title={'Enter dimension in inches'}
                                          />
                                      )}
                            </div>
                        )
                    );
                })}
            </div>
        );
    }

    // function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
    //     if (quickview === null) {
    //         return undefined;
    //     }
    //     const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
    //         return {
    //             DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
    //             DimensionValue: {
    //                 RecordId: 0,
    //                 Value: swatches.value
    //             }
    //         };
    //     });
    //     return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    // }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    function updateCustomCartDescription(length: number, width: number, qty: number): void {
        const weightResult = getProductWeight(cartProduct, product!, length, width, qty);
        const lfsfResult = getProductLFSF(cartProduct, product!, length, width, qty);
        if (customEnabled && cartProduct && (customLengthEnabled || customWidthEnabled)) {
            let customCutValues: customcutInterface = {
                ProductName: product?.Name,
                CustomLength: customLengthEnabled ? customLength : productLength,
                CustomWidth: customWidthEnabled ? customWidth : productWidth,
                Quantity: qty,
                LfsfValue: lfsfResult.value,
                Weight: weightResult.value
            };
            cartProduct!.Description = JSON.stringify(customCutValues);
        } else {
            let customCutValues: customcutInterface = {
                ProductName: product?.Name,
                CustomLength: selectedUnit === 'pc' ? 0 : selectedUnit === 'in' ? lfsfResult.value : lfsfResult.value * 12,
                CustomWidth: 0,
                Quantity: qty,
                LfsfValue: lfsfResult.value,
                Weight: weightResult.value
            };
            cartProduct!.Description = JSON.stringify(customCutValues);
        }
    }

    function renderQuantity(arg0: number): JSX.Element | null | undefined {
        const qtyIncrement = () => {
            if (qtyStep > 1) {
                setQuantity(prevCount => prevCount + qtyStep);
            } else {
                if (quantity < maxQuantity) {
                    setQuantity(prevCount => prevCount + 1);
                    updateCustomCartDescription(customLength, customWidth, quantity + 1);
                }
            }
        };
        const qtyDecrement = () => {
            if (qtyStep > 1) {
                setQuantity(prevCount => prevCount - qtyStep);
            } else {
                if (quantity >= 1) {
                    setQuantity(prevCount => prevCount - 1);
                    updateCustomCartDescription(customLength, customWidth, quantity - 1);
                }
            }
        };

        const checkQuantity = (qtyText: string) => {
            let qtyValue = parseInt(qtyText);
            setQuantity(qtyValue);
            updateCustomCartDescription(customLength, customWidth, qtyValue);
        };
        return (
            <div
                className='msc_quantity'
                title={
                    cartProduct
                        ? productAvailableQuantity && productAvailableQuantity!.IsProductAvailable
                            ? 'Choose quantity'
                            : 'Out of stock'
                        : 'Choose a variant'
                }
            >
                <button onClick={qtyDecrement} disabled={!!!productAvailableQuantity}>
                    <span>-</span>
                </button>

                <input
                    type='text'
                    className='msc_quantity_text_box'
                    onChange={e => checkQuantity(e.target.value)}
                    readOnly={qtyReadonly}
                    disabled={!!!productAvailableQuantity}
                    value={quantity > 0 ? quantity.toString() : ''}
                />
                <button onClick={qtyIncrement} disabled={!!!productAvailableQuantity}>
                    <span>+</span>
                </button>
            </div>
        );
    }

    // function truncateStr(text: string, lenthTobeShown: number): string {
    //     if (text.length < lenthTobeShown) {
    //         return text;
    //     } else {
    //         return text.substring(0, lenthTobeShown) + '...';
    //     }
    // }

    //function formatNumber(value: number): string {
    //return new Intl.NumberFormat('en-US', { style: 'decimal' }).format(value);
    //}

    function getProductWeight(
        product: SimpleProduct | undefined,
        gridProduct: ProductSearchResult,
        customProductLength: number,
        customProductWidth: number,
        qty: number
    ): { value: number; display: string } {
        var weightUom = 'lb';
        let conversionValues: ProductConversionMatrix[] = [];
        if (product?.ExtensionProperties) {
            var extension = product.ExtensionProperties.find(ext => ext.Key === 'CONVERSIONMATRIX');
            if (extension?.Value?.StringValue) {
                conversionValues = JSON.parse(extension.Value.StringValue);
            }
        }

        if (conversionValues.length > 0) {
            var productUnit = gridProduct.DefaultUnitOfMeasure;
            var conversionValue = conversionValues.find(cv => cv.FromUnit === productUnit && cv.ToUnit === weightUom);
            if (conversionValue) {
                var weightValue = conversionValue.FACTOR;
                weightValue = (qty / qtyStep) * weightValue;
                return {
                    value: weightValue,
                    display: weightValue.toFixed(2).toString()
                };
            }
        }

        return { value: 0, display: '' };
    }

    function getProductLFSF(
        product: SimpleProduct | undefined,
        gridProduct: ProductSearchResult,
        customProductLength: number,
        customProductWidth: number,
        qty: number
    ): { value: number; display: string } {
        var lengthUom = 'ft';
        let conversionValues: ProductConversionMatrix[] = [];
        if (product?.ExtensionProperties) {
            var extension = product.ExtensionProperties.find(ext => ext.Key === 'CONVERSIONMATRIX');
            if (extension?.Value?.StringValue) {
                conversionValues = JSON.parse(extension.Value.StringValue);
            }
        }

        if (conversionValues.length > 0) {
            var productUnit = gridProduct.DefaultUnitOfMeasure;
            var conversionValue = conversionValues.find(cv => {
                if (cv.FromUnit === productUnit && lengthUom == 'sqft' && cv.ToUnit === 'sqin') {
                    return true;
                }
                return cv.FromUnit === productUnit && cv.ToUnit === lengthUom;
            });

            if (conversionValue) {
                var lfsfvalue = conversionValue.FACTOR;
                lfsfvalue = (qty / qtyStep) * lfsfvalue;
                return {
                    value: lfsfvalue,
                    display: lfsfvalue.toFixed(2).toString() + ' ' + lengthUom
                };
            }
        }

        return { value: 0, display: '' };
    }

    return (
        <>
            {isMobile ? (
                <>
                    <div
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product-Name'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context
                                )}
                            </div>
                            <div className='msc-product__details'>
                                <a href={'#'}>
                                    <span className='msc-product__title' title={product.Name}>
                                        {product.Name}
                                    </span>
                                </a>
                            </div>
                        </div>
                        {defaultDimensions && renderDimensions()}
                        {renderQuantity(0)}
                        <div className='msc-product-cart'>
                            <div className='msc-product-variant-info'>
                                {cartProduct && (
                                    <div className='product_weight'>
                                        Weight: {getProductWeight(cartProduct, product, customLength, customWidth, quantity).display} lbs
                                    </div>
                                )}
                                <br />
                                {cartProduct && (
                                    <div className='product_lfsf'>
                                        LF/SF: {getProductLFSF(cartProduct, product, customLength, customWidth, quantity).display}
                                    </div>
                                )}
                            </div>
                            {cartProduct && quantity > 0 && quantity <= maxQuantity ? (
                                <Node className='mobile-cart' key={quantity}>
                                    <AddToCartComponent
                                        addToCartText='Add'
                                        outOfStockText='Add'
                                        quantity={quantity}
                                        productAvailability={{
                                            AvailableQuantity: maxQuantity
                                        }}
                                        data={{ product: cartProduct }}
                                        context={context}
                                        id={id}
                                        typeName={typeName}
                                    />
                                </Node>
                            ) : (
                                <div className='mobile-cart'>
                                    <button className='msc-add-to-cart ' disabled title='Add to cart'>
                                        Add
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
                    <div className='msc-product__image'>
                        {renderProductPlacementImage(
                            imageSettings,
                            context.request.gridSettings,
                            productImageUrl,
                            product.PrimaryImageUrl,
                            product.Name,
                            context
                        )}
                    </div>
                    <div className='msc-product__details'>
                        <a href={'#'}>
                            <h5 className='msc-product__title' title={product.Name}>
                                {product.Name}
                            </h5>
                        </a>
                    </div>
                    {showCustomerPartNumber && <span className='msc-product__cpn-text'>{customerPartNumber}</span>}

                    {defaultDimensions && renderDimensions()}
                    <div className='msc-product__qty'>{!(customLengthEnabled || customWidthEnabled) && renderQuantity(0)}</div>
                    <div className='msc-product__uom'>
                        <span className='product_weight'>
                            {cartProduct && (
                                <>{`Total Weight: ${
                                    getProductWeight(cartProduct, product, customLength, customWidth, quantity).display
                                } lbs`}</>
                            )}
                        </span>
                        <span className='product_lfsf'>
                            {cartProduct && (
                                <>{`Total FT / SQFT: ${
                                    getProductLFSF(cartProduct, product, customLength, customWidth, quantity).display
                                }`}</>
                            )}
                        </span>
                    </div>
                    {isLeadTimeEnabled && (
                        <div className='msc-product__lead-time'>
                            {leadTime && <span className='lead-time-text'>{leadTime}</span>}
                            {quantity > maxQuantity && (
                                <div className='msc_quantity_error'>
                                    <span>Quantity unavailable.</span>
                                    <span>Available quantity: {maxQuantity}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {showUnitOfMeasure && !(customLengthEnabled || customWidthEnabled) && (
                        <div className='product-unit-of-measure'>
                            {unitsOfMeasure && unitsOfMeasure.length > 0 && renderUnitOfMeasure()}
                        </div>
                    )}
                    {cartProduct && quantity > 0 && !(customLengthEnabled || customWidthEnabled) ? (
                        <Node className='ms-search-result-container__add-to-cart-container' key={quantity}>
                            <AddToCartComponent
                                addToCartText='Add'
                                outOfStockText='Add'
                                quantity={quantity}
                                productAvailability={{
                                    AvailableQuantity: maxQuantity
                                }}
                                data={{ product: cartProduct }}
                                context={context}
                                id={id}
                                typeName={typeName}
                                onAdd={() => {
                                    setCustomLength(0);
                                    setCustomWidth(0);
                                    setQuantity(0);
                                }}
                            />
                        </Node>
                    ) : (customLengthEnabled || customWidthEnabled) && cartProduct ? (
                        <div className='ms-search-result-container__add-to-cart-container'>
                            <button
                                className='msc-add-to-cart '
                                title='Add to cart'
                                onClick={() => {
                                    setIsPopupOpen(!isPopupOpen);
                                }}
                            >
                                Configure
                            </button>
                            {isPopupOpen &&
                                ReactDOM.createPortal(
                                    <div className='popup-overlay'>
                                        <div
                                            className='custom-popup'
                                            style={{
                                                display: isPopupOpen ? 'block' : 'none',
                                                position: 'fixed', // changed from 'absolute'
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 9999,
                                                background: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            {getExtensionPropertyValue(cartProduct, 'ATTRIBVALUERELATION') === 'Coil' && (
                                                <SlitCoilPopup
                                                    isOpen={isPopupOpen}
                                                    slitTolerance={getExtensionPropertyValue(cartProduct, 'SLITTOLERANCE') as string}
                                                    standardInnerDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'STANDARDINNERDIAMETER') as number
                                                    }
                                                    standardOuterDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'STANDARDOUTERDIAMETER') as number
                                                    }
                                                    minInnerDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'MINIMUMINNERDIAMETER') as number
                                                    }
                                                    maxInnerDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'MAXIMUMINNERDIAMETER') as number
                                                    }
                                                    minOuterDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'MINIMUMOUTERDIAMETER') as number
                                                    }
                                                    maxOuterDiameter={
                                                        getExtensionPropertyValue(cartProduct, 'MAXIMUMOUTERDIAMETER') as number
                                                    }
                                                    minWidth={getExtensionPropertyValue(cartProduct, 'MINWIDTH') as number}
                                                    maxWidth={getExtensionPropertyValue(cartProduct, 'MAXWIDTH') as number}
                                                    productName={product.Name}
                                                    context={context}
                                                    productId={cartProduct!.RecordId}
                                                    catalogId={getCatalogId(context.request)}
                                                    onDismiss={() => setIsPopupOpen(false)}
                                                />
                                            )}
                                        </div>
                                    </div>,
                                    document.body
                                )}
                        </div>
                    ) : (
                        <div className='ms-search-result-container__add-to-cart-container'>
                            <button className='msc-add-to-cart ' disabled title='Add to cart'>
                                Add
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
